

import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { fetchDashboardSummary } from "../../../redux/slices/adminDashboardSummarySlice";
import { fetchEmployees } from "../../../redux/slices/adminEmployeesSlice";
import { 
  fetchEmployeePerformance, 
  clearPerformance,
  setRange 
} from "../../../redux/slices/employeePerformanceSlice";

const DashboardPage = () => {
  const dispatch = useDispatch();
  
  const [time, setTime] = useState(new Date());
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [showPerformanceModal, setShowPerformanceModal] = useState(false);
  
  const authState = useSelector(state => state.auth);
  const currentUser = authState?.user || null;
  
  const summaryState = useSelector(state => state.adminDashboardSummary);
  const { 
    loading: summaryLoading, 
    error: summaryError, 
    summary: dashboardSummary 
  } = summaryState || {};
  
  const employeesState = useSelector(state => state.adminEmployees);
  const { 
    employees = [], 
    loading: employeesLoading,
    error: employeesError
  } = employeesState || {};
  
  const performanceState = useSelector(state => state.employeePerformance);
  const { 
    loading: perfLoading, 
    employee: perfEmployee, 
    performance, 
    range = 'day',
    error: perfError 
  } = performanceState || {};
  
  useEffect(() => {
    dispatch(fetchDashboardSummary());
    dispatch(fetchEmployees());
  }, [dispatch]);
  
  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);
  
  const handleEmployeeClick = (employeeId) => {
    if (!employeeId) return;
    dispatch(clearPerformance());
    setSelectedEmployeeId(employeeId);
    setShowPerformanceModal(true);
    dispatch(fetchEmployeePerformance({ 
      employeeId, 
      range: 'day' 
    }));
  };
  
  const handleRangeChange = (newRange) => {
    dispatch(setRange(newRange));
    if (selectedEmployeeId) {
      dispatch(fetchEmployeePerformance({ 
        employeeId: selectedEmployeeId, 
        range: newRange 
      }));
    }
  };
  
  const closeModal = () => {
    setShowPerformanceModal(false);
    setSelectedEmployeeId(null);
    dispatch(clearPerformance());
  };
  
  if (employeesLoading || summaryLoading) {
    return (
      <div className="page-content">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading dashboard...</p>
        </div>
        <style>{`
          .page-content {
            padding: 24px;
            min-height: 100vh;
            background: #0f172a;
          }
          .loading-state {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            height: 400px;
            color: #94a3b8;
          }
          .spinner {
            width: 50px;
            height: 50px;
            border: 4px solid #334155;
            border-top-color: #3b82f6;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin-bottom: 16px;
          }
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }
  
  if (employeesError || summaryError) {
    return (
      <div className="page-content">
        <div className="error-state">
          <p>❌ {employeesError || summaryError}</p>
          <button 
            className="retry-btn"
            onClick={() => {
              dispatch(fetchDashboardSummary());
              dispatch(fetchEmployees());
            }}
          >
            Retry
          </button>
        </div>
        <style>{`
          .page-content {
            padding: 24px;
            min-height: 100vh;
            background: #0f172a;
          }
          .error-state {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            height: 400px;
            color: #ef4444;
            text-align: center;
          }
          .retry-btn {
            margin-top: 16px;
            padding: 8px 20px;
            background: #3b82f6;
            color: white;
            border: none;
            border-radius: 6px;
            cursor: pointer;
          }
        `}</style>
      </div>
    );
  }
  
  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <h2 className="page-title">📊 Dashboard Overview</h2>
          <p className="page-subtitle">Welcome back! Today is {time.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>
        <div className="current-time">
          <span className="time-icon">🕐</span>
          <span>{time.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
        </div>
      </div>
      
      <div className="summary-cards">
        <div className="card">
          <div className="card-icon">👥</div>
          <h3>Total Employees</h3>
          <p className="card-value">
            {/* {dashboardSummary?.totalEmployees || 0} */}
            {employees?.length || 0}
            </p>
        </div>
        
        {/* <div className="card">
          <div className="card-icon">✅</div>
          <h3>Active Today</h3>
          <p className="card-value">{dashboardSummary?.activeEmployees || 0}</p>
        </div> */}
        
        {/* <div className="card">
          <div className="card-icon">❌</div>
          <h3>Absent Today</h3>
          <p className="card-value">{dashboardSummary?.absentEmployees || 0}</p>
        </div> */}
        
        {/* <div className="card">
          <div className="card-icon">⏱️</div>
          <h3>Avg Hours Today</h3>
          <p className="card-value">{dashboardSummary?.avgHoursToday || 0}h</p>
        </div> */}
      </div>
      
      <div className="employees-section">
        <div className="section-header">
          <h3>👥 All Employees</h3>
          <div className="total-count">{employees.length} employees</div>
        </div>
        
        {employees.length === 0 ? (
          <div className="empty-state">
            <p>No employees found</p>
          </div>
        ) : (
          <div className="employees-grid">
            {employees.map(emp => (
              <div 
                key={emp._id} 
                className="employee-card clickable"
                onClick={() => handleEmployeeClick(emp._id)}
              >
                <div className="employee-avatar">
                  {emp.name?.[0]?.toUpperCase() || 'E'}
                </div>
                <div className="employee-details">
                  <h4 className="employee-name">{emp.name || 'Unknown'}</h4>
                  <p className="employee-role">{emp.role || 'No role'}</p>
                  <p className="employee-department">{emp.department || 'No department'}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {showPerformanceModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2>📊 Employee Performance</h2>
                {perfEmployee && (
                  <p className="modal-subtitle">{perfEmployee.name} • {perfEmployee.email}</p>
                )}
              </div>
              <button className="modal-close" onClick={closeModal}>✕</button>
            </div>
            
            <div className="range-selector">
              <button 
                className={`range-btn ${range === 'day' ? 'active' : ''}`}
                onClick={() => handleRangeChange('day')}
              >
                Daily
              </button>
              <button 
                className={`range-btn ${range === 'week' ? 'active' : ''}`}
                onClick={() => handleRangeChange('week')}
              >
                Weekly
              </button>
              <button 
                className={`range-btn ${range === 'month' ? 'active' : ''}`}
                onClick={() => handleRangeChange('month')}
              >
                Monthly
              </button>
              <button 
                className={`range-btn ${range === 'year' ? 'active' : ''}`}
                onClick={() => handleRangeChange('year')}
              >
                Yearly
              </button>
            </div>
            
            {perfLoading ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading performance data...</p>
              </div>
            ) : perfError ? (
              <div className="error-state">
                <p>❌ {perfError}</p>
              </div>
            ) : performance ? (
              <div className="performance-content">
                <div className="perf-summary">
                  <div className="perf-card">
                    <div className="perf-icon">⏱️</div>
                    <div>
                      <div className="perf-label">Total Hours</div>
                      <div className="perf-value">{performance.totalHours || 0}h</div>
                    </div>
                  </div>
                  <div className="perf-card">
                    <div className="perf-icon">📅</div>
                    <div>
                      <div className="perf-label">Active Days</div>
                      <div className="perf-value">{performance.activeDays || 0}</div>
                    </div>
                  </div>
                  <div className="perf-card">
                    <div className="perf-icon">📈</div>
                    <div>
                      <div className="perf-label">Avg Hours/Day</div>
                      <div className="perf-value">{performance.avgHoursPerDay || 0}h</div>
                    </div>
                  </div>
                  <div className="perf-card">
                    <div className="perf-icon">🎯</div>
                    <div>
                      <div className="perf-label">Target Achievement</div>
                      <div className="perf-value">{performance.targetAchievement || 0}%</div>
                    </div>
                  </div>
                </div>
                
                {performance.data && (
                  <div className="performance-data">
                    <h3>Performance Data</h3>
                    <pre className="data-raw">
                      {JSON.stringify(performance.data, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            ) : (
              <div className="empty-state">
                <p>No performance data available</p>
              </div>
            )}
          </div>
        </div>
      )}
      
      <style>{`
        .page-content {
          padding: 24px;
          min-height: 100vh;
          background: #0f172a;
          color: #f1f5f9;
        }
        
        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 32px;
          flex-wrap: wrap;
          gap: 16px;
        }
        
        .page-title {
          margin: 0;
          font-size: 28px;
          font-weight: 700;
          color: #f1f5f9;
        }
        
        .page-subtitle {
          margin: 8px 0 0 0;
          color: #94a3b8;
          font-size: 14px;
        }
        
        .current-time {
          display: flex;
          align-items: center;
          gap: 8px;
          background: rgba(59, 130, 246, 0.1);
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 14px;
          color: #3b82f6;
        }
        
        .summary-cards {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 20px;
          margin-bottom: 40px;
        }
        
        .card {
          background: #1e293b;
          border: 1px solid #334155;
          border-radius: 12px;
          padding: 24px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        
        .card-icon {
          font-size: 28px;
        }
        
        .card h3 {
          margin: 0;
          color: #94a3b8;
          font-size: 16px;
          font-weight: 500;
        }
        
        .card-value {
          font-size: 32px;
          font-weight: 700;
          color: #f1f5f9;
          margin: 0;
        }
        
        .employees-section {
          background: #1e293b;
          border: 1px solid #334155;
          border-radius: 12px;
          padding: 24px;
          margin-bottom: 40px;
        }
        
        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }
        
        .section-header h3 {
          margin: 0;
          font-size: 20px;
          color: #f1f5f9;
        }
        
        .total-count {
          background: rgba(59, 130, 246, 0.1);
          color: #3b82f6;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 14px;
          font-weight: 500;
        }
        
        .employees-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 20px;
        }
        
        .employee-card {
          background: #0f172a;
          border: 1px solid #334155;
          border-radius: 12px;
          padding: 20px;
          transition: all 0.2s ease;
        }
        
        .employee-card.clickable {
          cursor: pointer;
        }
        
        .employee-card.clickable:hover {
          transform: translateY(-4px);
          border-color: #3b82f6;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        }
        
        .employee-avatar {
          width: 50px;
          height: 50px;
          border-radius: 50%;
          background: #3b82f6;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          font-weight: 600;
          margin-bottom: 16px;
        }
        
        .employee-name {
          margin: 0 0 8px 0;
          font-size: 18px;
          font-weight: 600;
          color: #f1f5f9;
        }
        
        .employee-role {
          margin: 0 0 4px 0;
          color: #94a3b8;
          font-size: 14px;
        }
        
        .employee-department {
          margin: 0 0 12px 0;
          color: #64748b;
          font-size: 13px;
        }
        
        .empty-state {
          padding: 40px;
          text-align: center;
          color: #64748b;
          background: #0f172a;
          border-radius: 12px;
          border: 1px solid #334155;
        }
        
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }
        
        .modal-content {
          background: #1e293b;
          border-radius: 16px;
          width: 100%;
          max-width: 900px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
        }
        
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding: 24px;
          border-bottom: 1px solid #334155;
        }
        
        .modal-header h2 {
          margin: 0;
          color: #f1f5f9;
          font-size: 24px;
        }
        
        .modal-subtitle {
          color: #94a3b8;
          font-size: 14px;
          margin: 4px 0 0 0;
        }
        
        .modal-close {
          background: transparent;
          border: none;
          color: #94a3b8;
          font-size: 24px;
          cursor: pointer;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          transition: all 0.2s;
        }
        
        .modal-close:hover {
          background: rgba(239, 68, 68, 0.2);
          color: #ef4444;
        }
        
        .range-selector {
          display: flex;
          gap: 8px;
          padding: 16px 24px;
          background: #0f172a;
        }
        
        .range-btn {
          flex: 1;
          padding: 10px 20px;
          border: 1px solid #334155;
          background: transparent;
          color: #94a3b8;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 14px;
          font-weight: 500;
        }
        
        .range-btn:hover {
          background: rgba(59, 130, 246, 0.1);
          border-color: #3b82f6;
          color: #3b82f6;
        }
        
        .range-btn.active {
          background: #3b82f6;
          border-color: #3b82f6;
          color: white;
        }
        
        .performance-content {
          padding: 24px;
        }
        
        .perf-summary {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 16px;
          margin-bottom: 32px;
        }
        
        .perf-card {
          background: #0f172a;
          padding: 20px;
          border-radius: 12px;
          display: flex;
          gap: 16px;
          align-items: center;
          border: 1px solid #334155;
        }
        
        .perf-icon {
          font-size: 32px;
        }
        
        .perf-label {
          color: #94a3b8;
          font-size: 13px;
          margin-bottom: 4px;
        }
        
        .perf-value {
          color: #f1f5f9;
          font-size: 24px;
          font-weight: 700;
        }
        
        .performance-data {
          margin-top: 24px;
        }
        
        .performance-data h3 {
          color: #f1f5f9;
          margin-bottom: 16px;
        }
        
        .data-raw {
          background: #0f172a;
          border: 1px solid #334155;
          border-radius: 8px;
          padding: 16px;
          color: #94a3b8;
          font-size: 13px;
          overflow-x: auto;
          white-space: pre-wrap;
          word-wrap: break-word;
        }
        
        @media (max-width: 768px) {
          .summary-cards {
            grid-template-columns: repeat(2, 1fr);
          }
          
          .employees-grid {
            grid-template-columns: 1fr;
          }
          
          .range-selector {
            flex-wrap: wrap;
          }
          
          .range-btn {
            flex: 0 0 calc(50% - 4px);
          }
          
          .perf-summary {
            grid-template-columns: 1fr;
          }
        }
        
        @media (max-width: 480px) {
          .summary-cards {
            grid-template-columns: 1fr;
          }
          
          .page-header {
            flex-direction: column;
            align-items: flex-start;
          }
          
          .section-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
          }
        }
      `}</style>
    </div>
  );
};

export default DashboardPage;

